import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { Subscription, of } from 'rxjs';
import { catchError, finalize, map, switchMap, timeout } from 'rxjs/operators';
import { OnboardingStateService } from '../../services/onboarding-state.service';
import { AuthService } from '../../services/auth.service';
import { StudentService } from '../../services/student.service';
import { VolunteerService } from '../../services/volunteer.service';

@Component({
  selector: 'app-expediente-recibido',
  standalone: true,
  imports: [CommonModule, RouterModule],
  template: `
    <div class="onboarding-page-shell expediente-shell">
      <!-- 1. MISMO NAVBAR SUPERIOR DE 4 FASES (100% en verde, aparece UNA SOLA VEZ y SOLO en estado pendiente) -->
      @if (seqStep !== 'done') {
        <header
          class="onboarding-sticky-header expediente-top-navbar"
          [class.is-fading]="seqStep === 'fading'"
          aria-label="Progreso general: onboarding y registro completados"
        >
          <div class="onboarding-header-inner">
            <div class="onboarding-phase-grid">
              @for (phase of completedPhasesList; track phase.n) {
                <div class="onboarding-phase-col completed">
                  <div class="onboarding-col-title-row">
                    <span class="onboarding-phase-tag">{{ phase.title }}</span>
                    <span class="onboarding-phase-status-text">{{ phase.sub }}</span>
                  </div>
                  <div class="onboarding-bar-track">
                    <div class="onboarding-bar-fill fill-green" style="width: 100%;"></div>
                  </div>
                  <div class="onboarding-subsegments">
                    @for (seg of phase.segments; track $index) {
                      <div class="segment-pill green">
                        <div class="segment-pill-inner fill-green" style="width: 100%;"></div>
                      </div>
                    }
                  </div>
                </div>
              }
            </div>
          </div>
        </header>
      }

      <!-- Confeti CSS ligero disparado una sola vez -->
      @if (showConfettiBurst) {
        <div
          aria-hidden="true"
          style="position: fixed; inset: 0; pointer-events: none; z-index: 9999; overflow: hidden;"
        >
          @for (piece of confettiPieces; track piece.id) {
            <span
              [style.left.%]="piece.left"
              [style.background]="piece.color"
              [style.animationDelay.ms]="piece.delay"
              style="position: absolute; top: -16px; width: 10px; height: 16px; border-radius: 3px; animation: abaqConfettiFall 2.2s ease-out forwards;"
            ></span>
          }
        </div>
        <style>
          @keyframes abaqConfettiFall {
            0% {
              transform: translateY(0) rotate(0deg);
              opacity: 1;
            }
            100% {
              transform: translateY(92vh) rotate(540deg);
              opacity: 0;
            }
          }
        </style>
      }

      <!-- 2. TARJETA CENTRAL DE EXPEDIENTE (REACTIVA EN TIEMPO REAL A LA BASE DE DATOS) -->
      <div class="expediente-recibido-viewport">
        <div class="expediente-container-inner" [class.expediente-status-pending]="currentAccountStatus === 'pending'">
          <div class="expediente-main-card">
            <div
              class="expediente-top-stripe"
              [class.stripe-active]="currentAccountStatus === 'active'"
              [class.stripe-rejected]="currentAccountStatus === 'rejected'"
              aria-hidden="true"
            ></div>

            <div class="expediente-card-body">
              <!-- Encabezado de Marca & Badge de Estado -->
              <div class="expediente-brand-row">
                <div class="expediente-brand-center">
                  <span class="expediente-brand-wordmark">ABAQ</span>
                  <span class="expediente-brand-sub">
                    {{ userType === 'SS' ? 'SERVICIO SOCIAL · QUERÉTARO' : 'VOLUNTARIADO GENERAL · QUERÉTARO' }}
                  </span>
                </div>

                <!-- Badge según estado DB -->
                @if (currentAccountStatus === 'active') {
                  <div class="expediente-badge-pill pill-active">
                    <span class="material-symbols-outlined text-sm">verified</span>
                    <span>EXPEDIENTE APROBADO Y CUENTA ACTIVADA</span>
                  </div>
                } @else if (currentAccountStatus === 'rejected') {
                  <div class="expediente-badge-pill pill-rejected">
                    <span class="material-symbols-outlined text-sm">cancel</span>
                    <span>SOLICITUD NO APROBADA</span>
                  </div>
                } @else {
                  <div class="expediente-active-pill">
                    <span class="relative-pulse-box">
                      <span class="pulse-beacon-ping"></span>
                      <span class="pulse-beacon-dot"></span>
                    </span>
                    <span>EXPEDIENTE REGISTRADO (PENDIENTE)</span>
                  </div>
                }
              </div>

              <!-- ========================================== -->
              <!-- CASO 1: ESTADO PENDIENTE (ESPERANDO CAMBIO DB) -->
              <!-- ========================================== -->
              @if (currentAccountStatus === 'pending') {
                <div class="expediente-grid-columns">
                  <!-- Columna izquierda -->
                  <div class="expediente-col-left">
                    <div class="expediente-status-pill">
                      <span class="status-pulse-dot"></span>
                      <span class="status-pill-text">En revisión por la coordinación</span>
                    </div>

                    <h1 class="expediente-heading-title">
                      ¡Felicidades! Expediente recibido con éxito
                    </h1>

                    <div class="expediente-folio-box">
                      <span class="folio-label">Número de folio:</span>
                      <strong class="folio-code expediente-folio-badge">{{ folio }}</strong>
                      <button
                        type="button"
                        class="folio-copy-btn"
                        (click)="copyFolio()"
                      >
                        <span class="material-symbols-outlined text-sm">
                          {{ isCopied ? 'check' : 'content_copy' }}
                        </span>
                        <span>{{ isCopied ? '¡Copiado!' : 'Copiar' }}</span>
                      </button>
                    </div>

                    <p class="expediente-reassurance-text">
                      Recibimos la totalidad de tu proceso de onboarding y registro. La coordinación de ABAQ está revisando tus datos y enlaces de nube; cuando tu solicitud sea aceptada recibirás automáticamente un correo (vía Resend) y esta pantalla se actualizará para que puedas descargar tu <strong>Carta de Aceptación y Felicitación</strong> e ingresar a la plataforma.
                    </p>

                    <div class="expediente-student-meta">
                      <div style="display: flex; align-items: center; gap: 8px; flex-wrap: wrap;">
                        <span class="material-symbols-outlined text-sm text-navy">
                          {{ userType === 'SS' ? 'school' : 'volunteer_activism' }}
                        </span>
                        <span class="meta-student-name">{{ fullName }}</span>
                        @if (email) {
                          <span class="meta-student-email">({{ email }})</span>
                        }
                      </div>

                      @if (fechaEmisionDisplay) {
                        <div class="meta-expediente-timestamps">
                          <span class="timestamp-badge">
                            <span class="material-symbols-outlined text-xs">event_available</span>
                            <span>Emisión de expediente: <strong>{{ fechaEmisionDisplay }}</strong></span>
                          </span>
                          @if (tiempoLlenadoDisplay) {
                            <span class="timestamp-badge">
                              <span class="material-symbols-outlined text-xs">timer</span>
                              <span>Tiempo de llenado: <strong>{{ tiempoLlenadoDisplay }}</strong></span>
                            </span>
                          }
                        </div>
                      }
                    </div>
                  </div>

                  <!-- Columna derecha -->
                  <div
                    class="expediente-col-right"
                    [class.col-right-vg]="userType === 'VG'"
                  >
                    @if (userType === 'SS') {
                      <div class="expediente-hint-card hint-card-hours-gradient">
                        <div class="hint-card-icon-wrap icon-spark-gradient">
                          <span class="hours-notice-spark" aria-hidden="true">✨</span>
                        </div>
                        <div class="hint-card-content">
                          <h3 class="hint-card-title text-white">10 horas de servicio social</h3>
                          <p class="hint-card-body text-white-90">
                            Si se acepta la solicitud de ingreso, se otorgan <strong>10 horas de servicio social</strong> por tu proceso de inducción.
                          </p>
                        </div>
                      </div>
                    }

                    <div class="expediente-hint-card hint-card-time">
                      <div class="hint-card-icon-wrap icon-green">
                        <span class="material-symbols-outlined">schedule</span>
                      </div>
                      <div class="hint-card-content">
                        <h3 class="hint-card-title">Respuesta en 24 a 48 horas hábiles</h3>
                        <p class="hint-card-body">
                          Las revisiones se atienden en <strong>estricto orden de registro</strong>.
                          Recibirás tu felicitación de aceptación con link de descarga directamente en tu correo y notificaciones.
                        </p>
                      </div>
                    </div>

                    @if (userType === 'SS') {
                      <div class="expediente-hint-card hint-card-email">
                        <div class="hint-card-icon-wrap icon-navy">
                          <span class="material-symbols-outlined">warning</span>
                        </div>
                        <div class="hint-card-content">
                          <h3 class="hint-card-title">NOTA SS — Regla de continuidad</h3>
                          <p class="hint-card-body">
                            <strong>2 meses de inactividad no previamente notificada se solicitará baja</strong>
                            de tu Servicio Social. ¿Algún dato está mal? Escribe a
                            <strong>admision.servicio&#64;abaq.org.mx</strong> indicando tu folio.
                          </p>
                        </div>
                      </div>
                    } @else {
                      <div class="expediente-hint-card hint-card-email">
                        <div class="hint-card-icon-wrap icon-navy">
                          <span class="material-symbols-outlined">info</span>
                        </div>
                        <div class="hint-card-content">
                          <h3 class="hint-card-title">NOTA VOLUNTARIADO GENERAL</h3>
                          <p class="hint-card-body">
                            Tu colaboración voluntaria apoya activamente las actividades de rescate y bienestar animal de ABAQ en Querétaro.
                            ¿Algún dato está mal? Escribe a
                            <strong>admision.servicio&#64;abaq.org.mx</strong> indicando tu folio.
                          </p>
                        </div>
                      </div>
                    }
                  </div>
                </div>

                <hr class="expediente-divider" />

                <div class="expediente-actions-bar">
                  <div class="actions-left-group">
                    <a
                      class="btn-expediente-portal"
                      [href]="'mailto:admision.servicio@abaq.org.mx?subject=Revision%20Folio%20' + folio"
                    >
                      <span class="material-symbols-outlined">mail</span>
                      <span>Contactar a coordinación</span>
                    </a>
                  </div>

                  <!-- Centro: Última fecha de actualización -->
                  <div class="expediente-footer-sync">
                    <span class="material-symbols-outlined footer-sync-icon" [class.spinning]="isCheckingStatus">autorenew</span>
                    <span class="footer-sync-text">
                      Última actualización: <strong>{{ lastCheckedTime || 'Comprobando...' }}</strong>
                    </span>
                    <button
                      type="button"
                      class="btn-sync-inline-refresh"
                      [disabled]="isCheckingStatus"
                      (click)="checkDbStatus(true)"
                      title="Comprobar cambios ahora"
                      aria-label="Comprobar cambios ahora"
                    >
                      <span class="material-symbols-outlined footer-refresh-icon" [class.spinning]="isCheckingStatus">refresh</span>
                    </button>
                  </div>

                  <button
                    type="button"
                    class="btn-expediente-logout"
                    (click)="logout()"
                  >
                    <span class="material-symbols-outlined text-sm">logout</span>
                    <span>Cerrar sesión</span>
                  </button>
                </div>
              }

              <!-- ========================================== -->
              <!-- CASO 2: ESTADO ACTIVO (APROBADO EN BD) -->
              <!-- ========================================== -->
              @else if (currentAccountStatus === 'active') {
                <div class="expediente-approved-view">
                  <div class="approved-hero-badge">
                    <div class="hero-icon-circle">
                      <span class="material-symbols-outlined icon-hero-check">check_circle</span>
                    </div>
                    <div class="hero-content">
                      <div class="hero-tag-pill">
                        <span class="material-symbols-outlined text-xs">verified</span>
                        <span>EXPEDIENTE FORMALMENTE APROBADO</span>
                      </div>
                      <h1 class="hero-title">¡Felicidades! Tu cuenta ha sido activada</h1>
                      <p class="hero-subtitle">
                        La coordinación de ABAQ Querétaro ha revisado y validado satisfactoriamente tus datos y documentos. Ya formas parte activa de la plataforma.
                      </p>
                    </div>
                  </div>

                  <div class="approved-meta-box">
                    <div class="meta-row-item">
                      <span class="meta-label">Folio institucional:</span>
                      <strong class="meta-val-code">{{ folio }}</strong>
                      <button
                        type="button"
                        class="folio-copy-btn"
                        (click)="copyFolio()"
                      >
                        <span class="material-symbols-outlined text-sm">
                          {{ isCopied ? 'check' : 'content_copy' }}
                        </span>
                        <span>{{ isCopied ? 'Copiado' : 'Copiar' }}</span>
                      </button>
                    </div>
                    <div class="meta-row-item">
                      <span class="meta-label">Titular:</span>
                      <strong class="meta-val-name">{{ fullName }}</strong>
                      @if (email) {
                        <span class="meta-val-email">({{ email }})</span>
                      }
                    </div>
                    @if (fechaAceptacionDisplay) {
                      <div class="meta-row-item">
                        <span class="meta-label">Expedido / Aceptado:</span>
                        <strong class="meta-val-code" style="color: #059669;">{{ fechaAceptacionDisplay }}</strong>
                      </div>
                    }
                    @if (fechaEmisionDisplay) {
                      <div class="meta-row-item">
                        <span class="meta-label">Emisión de registro:</span>
                        <span class="meta-val-email">{{ fechaEmisionDisplay }}</span>
                      </div>
                    }
                  </div>

                  @if (userType === 'SS') {
                    <div class="expediente-hours-notice-card card-approved">
                      <span class="hours-notice-spark" aria-hidden="true">✨</span>
                      <div class="hours-notice-text">
                        ¡Solicitud aceptada! Se han otorgado <strong>10 horas de servicio social</strong> a tu expediente por tu proceso de inducción.
                      </div>
                    </div>
                  }

                  <!-- Tarjeta de Carta de Aceptación -->
                  @if (cartaAceptacionUrl) {
                    <div class="carta-box-card">
                      <div class="carta-box-left">
                        <div class="carta-icon-wrap">
                          <span class="material-symbols-outlined text-2xl">workspace_premium</span>
                        </div>
                        <div class="carta-text-group">
                          <h3 class="carta-title">Carta de Aceptación y Felicitación Oficial</h3>
                          <p class="carta-desc">
                            Documento oficial con código criptográfico de autenticidad institucional ABAQ emitido para tu universidad.
                          </p>
                        </div>
                      </div>
                      <a
                        class="btn-carta-download"
                        [href]="cartaAceptacionUrl"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <span class="material-symbols-outlined text-sm">download</span>
                        <span>Descargar Carta Oficial</span>
                      </a>
                    </div>
                  }

                  @if (coordinatorComments) {
                    <div class="expediente-callout-box callout-welcome">
                      <div class="callout-header">
                        <span class="material-symbols-outlined text-sm">chat</span>
                        <strong>Mensaje de Bienvenida de la Coordinación:</strong>
                      </div>
                      <p class="callout-text">"{{ coordinatorComments }}"</p>
                    </div>
                  }

                  <hr class="expediente-divider" />

                  <!-- Barra de acciones principales -->
                  <div class="approved-actions-row">
                    <button
                      type="button"
                      class="btn-portal-enter"
                      (click)="goToPlatform()"
                    >
                      <span>Ingresar a la Plataforma ABAQ</span>
                      <span class="material-symbols-outlined text-base">arrow_forward</span>
                    </button>

                    <div class="approved-actions-secondary">
                      <a
                        class="btn-contact-secondary"
                        [href]="'mailto:admision.servicio@abaq.org.mx?subject=Consulta%20Folio%20' + folio"
                      >
                        <span class="material-symbols-outlined text-sm">mail</span>
                        <span>Soporte / Coordinación</span>
                      </a>

                      <button
                        type="button"
                        class="btn-expediente-logout"
                        (click)="logout()"
                      >
                        <span class="material-symbols-outlined text-sm">logout</span>
                        <span>Cerrar sesión</span>
                      </button>
                    </div>
                  </div>
                </div>
              }

              <!-- ========================================== -->
              <!-- CASO 3: ESTADO RECHAZADO (NO APROBADO EN BD) -->
              <!-- ========================================== -->
              @else {
                <div class="expediente-rejected-view">
                  <div class="rejected-hero-badge">
                    <div class="rejected-icon-circle">
                      <span class="material-symbols-outlined text-3xl">error</span>
                    </div>
                    <div>
                      <h1 class="hero-title text-red">Solicitud no aprobada</h1>
                      <p class="hero-subtitle">
                        La coordinación de ABAQ ha revisado la documentación y por el momento el expediente no fue aprobado. Puedes ponerte en contacto con nosotros para corregir la información de tus enlaces o documentación.
                      </p>
                    </div>
                  </div>

                  @if (coordinatorComments) {
                    <div class="expediente-callout-box callout-observations">
                      <div class="callout-header">
                        <span class="material-symbols-outlined text-sm">warning</span>
                        <strong>Observaciones de la Coordinación para Corrección:</strong>
                      </div>
                      <p class="callout-text">"{{ coordinatorComments }}"</p>
                      <small class="callout-subtext">
                        Si deseas reingresar tu expediente corregido o cambiar de modalidad, puedes acceder a la página de registro oficial de ABAQ (/auth/signup) utilizando este mismo correo electrónico.
                      </small>
                    </div>
                  }

                  <div class="expediente-folio-box">
                    <span class="folio-label">Número de folio:</span>
                    <strong class="folio-code expediente-folio-badge">{{ folio }}</strong>
                  </div>

                  <hr class="expediente-divider" />

                  <div class="expediente-actions-bar">
                    <a
                      class="btn-contact-rejected"
                      [href]="'mailto:admision.servicio@abaq.org.mx?subject=Aclaracion%20Expediente%20Folio%20' + folio"
                    >
                      <span class="material-symbols-outlined text-sm">mail</span>
                      <span>Contactar a Coordinación de Admisión</span>
                    </a>

                    <button
                      type="button"
                      class="btn-expediente-logout"
                      (click)="logout()"
                    >
                      <span class="material-symbols-outlined text-sm">logout</span>
                      <span>Cerrar sesión</span>
                    </button>
                  </div>
                </div>
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .expediente-top-navbar {
      position: sticky;
      top: 0;
      z-index: 50;
      background: #ffffff;
      border-bottom: 1px solid rgba(197, 197, 211, 0.4);
      box-shadow: 0 2px 10px rgba(20, 27, 43, 0.04);
      padding: 12px 24px;
      opacity: 1;
      transform: translateY(0);
      max-height: 120px;
      overflow: hidden;
      transition: opacity 0.65s cubic-bezier(0.4, 0, 0.2, 1),
                  transform 0.65s cubic-bezier(0.4, 0, 0.2, 1),
                  max-height 0.65s cubic-bezier(0.4, 0, 0.2, 1),
                  padding 0.65s cubic-bezier(0.4, 0, 0.2, 1),
                  margin 0.65s cubic-bezier(0.4, 0, 0.2, 1);

      &.is-fading {
        opacity: 0;
        transform: translateY(-20px);
        max-height: 0;
        padding-top: 0;
        padding-bottom: 0;
        margin-bottom: 0;
        border-bottom-color: transparent;
      }
    }

    .stripe-active {
      background: linear-gradient(90deg, #10b981 0%, #059669 100%) !important;
    }
    .stripe-rejected {
      background: linear-gradient(90deg, #ef4444 0%, #dc2626 100%) !important;
    }

    .expediente-badge-pill {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      font-size: 11px;
      font-weight: 800;
      padding: 5px 14px;
      border-radius: 9999px;
      letter-spacing: 0.3px;
    }
    .expediente-badge-pill.pill-active {
      color: #065f46;
      background: #d1fae5;
      border: 1px solid #a7f3d0;
    }
    .expediente-badge-pill.pill-rejected {
      color: #991b1b;
      background: #fee2e2;
      border: 1px solid #fecaca;
    }

    /* Expediente Timestamps & Metadatos */
    .meta-expediente-timestamps {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 8px;
      margin-top: 10px;
    }
    .timestamp-badge {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      font-size: 11.5px;
      color: #475569;
      background: #f1f5f9;
      padding: 4px 10px;
      border-radius: 8px;
      border: 1px solid #e2e8f0;
      line-height: 1.3;
    }
    .timestamp-badge strong {
      color: #1e293b;
      font-weight: 700;
    }

    /* Student 10 Hours Notice Card */
    .expediente-hours-notice-card {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 16px;
      border-radius: 12px;
      background: #eff6ff;
      border: 1.5px solid #bfdbfe;
      color: #1e40af;
      margin: 8px 0;
    }
    .expediente-hours-notice-card.card-approved {
      background: #ecfdf5;
      border-color: #a7f3d0;
      color: #065f46;
    }
    .hours-notice-spark {
      font-size: 20px;
      line-height: 1;
      flex-shrink: 0;
    }
    .hours-notice-text {
      font-size: 13px;
      line-height: 1.45;
    }

    /* 10 Horas Hint Card - Gradiente Azul y Verde ABAQ */
    .expediente-hint-card.hint-card-hours-gradient {
      background: linear-gradient(135deg, #1f2f63 0%, #24366f 35%, #2d6a2e 78%, #5ca83a 100%);
      border: 1px solid rgba(255, 255, 255, 0.25);
      box-shadow: 0 4px 16px rgba(36, 54, 111, 0.16);
      color: #ffffff;
    }
    .hint-card-hours-gradient .hint-card-icon-wrap.icon-spark-gradient {
      background: rgba(255, 255, 255, 0.22);
      backdrop-filter: blur(6px);
      -webkit-backdrop-filter: blur(6px);
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    }
    .hint-card-hours-gradient .hint-card-title.text-white {
      color: #ffffff;
      font-weight: 800;
      letter-spacing: -0.2px;
    }
    .hint-card-hours-gradient .hint-card-body.text-white-90 {
      color: rgba(255, 255, 255, 0.94);
      line-height: 1.45;
    }
    .hint-card-hours-gradient .hint-card-body strong {
      color: #ffffff;
      font-weight: 800;
      text-decoration: underline;
      text-decoration-color: rgba(255, 255, 255, 0.45);
      text-underline-offset: 2px;
    }

    /* Centrado vertical de columna derecha para Voluntario General */
    .expediente-grid-columns {
      align-items: stretch !important;
    }
    .expediente-col-right.col-right-vg {
      justify-content: center;
      height: 100%;
      min-height: 100%;
      gap: 18px;
    }

    /* Footer Sincronización en Tiempo Real */
    .expediente-footer-sync {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      font-size: 12.5px;
      color: #64748b;
      padding: 6px 14px;
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 9999px;
      user-select: none;
    }
    .footer-sync-icon {
      font-size: 16px;
      color: #5ca83a;
    }
    .footer-sync-text strong {
      color: #1e293b;
      font-weight: 700;
    }
    .btn-sync-inline-refresh {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 22px;
      height: 22px;
      padding: 0;
      border-radius: 50%;
      border: 1px solid #cbd5e1;
      background: #ffffff;
      color: #475569;
      cursor: pointer;
      transition: all 0.15s ease;
    }
    .btn-sync-inline-refresh:hover:not(:disabled) {
      background: #f1f5f9;
      color: #1e293b;
      border-color: #94a3b8;
    }
    .btn-sync-inline-refresh:disabled {
      opacity: 0.5;
      cursor: wait;
    }
    .footer-refresh-icon {
      font-size: 14px;
    }
    .spinning {
      animation: spinAnim 1.2s linear infinite;
      display: inline-block;
    }
    @keyframes spinAnim {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

    /* Approved State View */
    .expediente-approved-view {
      display: flex;
      flex-direction: column;
      gap: 24px;
    }
    .approved-hero-badge {
      display: flex;
      align-items: center;
      gap: 20px;
    }
    .hero-icon-circle {
      width: 64px;
      height: 64px;
      border-radius: 50%;
      background: #d1fae5;
      color: #059669;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    .icon-hero-check {
      font-size: 42px;
    }
    .hero-content {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }
    .hero-tag-pill {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      font-size: 11px;
      font-weight: 800;
      color: #065f46;
      background: #ecfdf5;
      padding: 3px 10px;
      border-radius: 9999px;
      align-self: flex-start;
      border: 1px solid #a7f3d0;
    }
    .hero-title {
      font-size: 26px;
      font-weight: 850;
      color: #1e293b;
      margin: 0;
      line-height: 1.25;
    }
    .hero-title.text-red {
      color: #b91c1c;
    }
    .hero-subtitle {
      font-size: 14px;
      color: #475569;
      margin: 0;
      line-height: 1.5;
    }
    .approved-meta-box {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 16px;
      padding: 14px 20px;
      border-radius: 12px;
      background: #f8fafc;
      border: 1px solid #e2e8f0;
    }
    .meta-row-item {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .meta-label {
      font-size: 13px;
      color: #64748b;
      font-weight: 600;
    }
    .meta-val-code {
      font-size: 14px;
      color: #0f172a;
      font-weight: 800;
      letter-spacing: 0.5px;
    }
    .meta-val-name {
      font-size: 14px;
      color: #0f172a;
      font-weight: 750;
    }
    .meta-val-email {
      font-size: 13px;
      color: #64748b;
    }

    /* Carta Box Card */
    .carta-box-card {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 20px;
      padding: 20px 24px;
      border-radius: 14px;
      background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
      border: 1px solid #bfdbfe;
    }
    .carta-box-left {
      display: flex;
      align-items: center;
      gap: 16px;
    }
    .carta-icon-wrap {
      width: 48px;
      height: 48px;
      border-radius: 12px;
      background: #2563eb;
      color: #ffffff;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    .carta-text-group {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
    .carta-title {
      font-size: 15px;
      font-weight: 800;
      color: #1e3a8a;
      margin: 0;
    }
    .carta-desc {
      font-size: 12.5px;
      color: #3b82f6;
      margin: 0;
      line-height: 1.4;
    }
    .btn-carta-download {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 10px 18px;
      border-radius: 10px;
      background: #1d4ed8;
      color: #ffffff;
      font-size: 13px;
      font-weight: 750;
      text-decoration: none;
      white-space: nowrap;
      transition: background 0.15s ease;
    }
    .btn-carta-download:hover {
      background: #1e40af;
    }

    /* Approved Actions Row */
    .approved-actions-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      flex-wrap: wrap;
    }
    .btn-portal-enter {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      padding: 14px 28px;
      border-radius: 12px;
      background: #059669;
      color: #ffffff;
      border: none;
      font-size: 15px;
      font-weight: 800;
      cursor: pointer;
      box-shadow: 0 4px 14px rgba(5, 150, 105, 0.3);
      transition: all 0.2s ease;
    }
    .btn-portal-enter:hover {
      background: #047857;
      transform: translateY(-1px);
      box-shadow: 0 6px 20px rgba(5, 150, 105, 0.4);
    }
    .approved-actions-secondary {
      display: flex;
      align-items: center;
      gap: 14px;
    }
    .btn-contact-secondary {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 10px 16px;
      border-radius: 10px;
      border: 1px solid #cbd5e1;
      background: #ffffff;
      color: #475569;
      font-size: 13px;
      font-weight: 650;
      text-decoration: none;
      transition: all 0.15s ease;
    }
    .btn-contact-secondary:hover {
      background: #f8fafc;
      color: #1e293b;
    }

    /* Rejected State View */
    .expediente-rejected-view {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }
    .rejected-hero-badge {
      display: flex;
      align-items: center;
      gap: 16px;
    }
    .rejected-icon-circle {
      width: 56px;
      height: 56px;
      border-radius: 50%;
      background: #fee2e2;
      color: #dc2626;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    .btn-contact-rejected {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 12px 20px;
      border-radius: 10px;
      background: #dc2626;
      color: #ffffff;
      font-size: 13.5px;
      font-weight: 700;
      text-decoration: none;
      transition: background 0.15s ease;
    }
    .btn-contact-rejected:hover {
      background: #b91c1c;
    }

    .expediente-callout-box {
      border-radius: 10px;
      padding: 14px 18px;
      margin: 12px 0 16px;
      font-size: 13.5px;
      line-height: 1.5;
    }
    .expediente-callout-box.callout-welcome {
      background: #f0fdf4;
      border-left: 4px solid #16a34a;
      color: #14532d;
    }
    .expediente-callout-box.callout-observations {
      background: #fef2f2;
      border-left: 4px solid #dc2626;
      color: #991b1b;
    }
    .callout-header {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-bottom: 6px;
      font-size: 13px;
    }
    .callout-text {
      margin: 0;
      font-style: italic;
      font-size: 14px;
    }
    .callout-subtext {
      display: block;
      margin-top: 8px;
      font-size: 12px;
      opacity: 0.85;
      font-style: normal;
    }

    @media (max-width: 768px) {
      .carta-box-card {
        flex-direction: column;
        align-items: flex-start;
      }
      .approved-actions-row {
        flex-direction: column;
        align-items: stretch;
      }
      .btn-portal-enter {
        justify-content: center;
      }
      .approved-actions-secondary {
        justify-content: space-between;
      }
      .expediente-actions-bar {
        flex-direction: column;
        align-items: stretch;
        gap: 12px;
      }
      .actions-left-group,
      .btn-expediente-logout,
      .btn-expediente-portal {
        width: 100%;
        justify-content: center;
      }
      .expediente-footer-sync {
        order: 2;
        width: 100%;
        justify-content: center;
      }
    }
  `],
})
export class ExpedienteRecibidoComponent implements OnInit, OnDestroy {
  private onboardingService = inject(OnboardingStateService);
  private authService = inject(AuthService);
  private studentService = inject(StudentService);
  private volunteerService = inject(VolunteerService);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);

  seqStep: 'green' | 'fading' | 'done' = 'done';
  showConfettiBurst = false;
  isCopied = false;
  isCheckingStatus = false;
  lastCheckedTime = '';

  currentAccountStatus: 'pending' | 'active' | 'rejected' = 'pending';
  dbFolio = '';
  dbFullName = '';
  dbEmail = '';
  cartaAceptacionUrl = '';
  coordinatorComments = '';
  dbFechaInicioFormulario?: string;
  dbFechaFinFormulario?: string;
  dbFechaEmision?: string;
  dbFechaAceptacion?: string;
  dbFechaExpedicion?: string;
  dbTiempoLlenadoMinutos?: number;

  private pollTimer?: ReturnType<typeof setInterval>;
  private fadeTimer?: ReturnType<typeof setTimeout>;
  private doneTimer?: ReturnType<typeof setTimeout>;
  private confettiEndTimer?: ReturnType<typeof setTimeout>;
  private userSub?: Subscription;

  private readonly ANIM_STORAGE_KEY = 'abaq_onboarding_anim_played';

  get completedPhasesList() {
    const isSS = this.userType === 'SS';
    return [
      {
        n: 1,
        title: isSS ? '✓ 01 Perfil Académico' : '✓ 01 Perfil Personal',
        sub: 'Completa',
        segments: [1, 2, 3, 4],
      },
      {
        n: 2,
        title: '✓ 02 Inducción',
        sub: 'Completa',
        segments: [1, 2, 3, 4, 5, 6],
      },
      { n: 3, title: '✓ 03 Sobre Mí', sub: 'Completa', segments: [1, 2, 3, 4] },
      { n: 4, title: '✓ 04 Documentos', sub: 'Completa', segments: [1, 2, 3] },
    ];
  }

  readonly confettiPieces = Array.from({ length: 42 }).map((_, idx) => ({
    id: idx,
    left: (idx * 2.4) % 100,
    delay: (idx % 7) * 90,
    color: ['#24366f', '#334c91', '#7bbf4f', '#5ca83a', '#cbe7bb'][idx % 5],
  }));

  get folio(): string {
    const currentUser = this.authService.getCurrentUser();
    return (
      this.dbFolio ||
      currentUser?.folio ||
      this.onboardingService.state.expediente.folio ||
      (this.userType === 'VG' ? 'VG-261031-01' : 'SS-261025-01')
    );
  }

  get userType(): 'SS' | 'VG' {
    const currentUser = this.authService.getCurrentUser();
    if (currentUser?.role === 'volunteer') return 'VG';
    if (currentUser?.role === 'student') return 'SS';
    if (this.dbFolio?.startsWith('VG-')) return 'VG';
    if (this.dbFolio?.startsWith('SS-')) return 'SS';
    const expFolio = this.onboardingService.state.expediente.folio;
    if (expFolio?.startsWith('VG-')) return 'VG';
    if (expFolio?.startsWith('SS-')) return 'SS';
    return this.onboardingService.state.phase1.userType || 'SS';
  }

  get fullName(): string {
    if (this.dbFullName) return this.dbFullName;
    const currentUser = this.authService.getCurrentUser();
    if (currentUser?.fullName) return currentUser.fullName;
    const p1 = this.onboardingService.state.phase1;
    const combined = `${p1.names || ''} ${p1.apellidoPaterno || ''} ${p1.apellidoMaterno || ''}`.trim();
    return combined || (this.userType === 'VG' ? 'Voluntario General ABAQ' : 'Aspirante Registrado ABAQ');
  }

  get email(): string {
    if (this.dbEmail) return this.dbEmail;
    const currentUser = this.authService.getCurrentUser();
    if (currentUser?.email) return currentUser.email;
    return this.onboardingService.state.phase1.email || '';
  }

  private formatFullDatetime(raw?: string | Date | null): string {
    if (!raw) return '';
    const d = new Date(raw);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  get fechaEmisionDisplay(): string {
    const raw =
      this.dbFechaEmision ||
      this.onboardingService.state.expediente.fechaEmisionExpediente ||
      this.onboardingService.state.expediente.fechaFinFormularioExpediente;
    return this.formatFullDatetime(raw);
  }

  get fechaAceptacionDisplay(): string {
    const raw =
      this.dbFechaAceptacion ||
      this.dbFechaExpedicion ||
      this.onboardingService.state.expediente.fechaAceptacionExpediente ||
      this.onboardingService.state.expediente.fechaExpedicionExpediente;
    return this.formatFullDatetime(raw);
  }

  get tiempoLlenadoDisplay(): string {
    const mins =
      this.dbTiempoLlenadoMinutos !== undefined
        ? this.dbTiempoLlenadoMinutos
        : this.onboardingService.state.expediente.tiempoLlenadoMinutosExpediente;
    if (mins === undefined || mins === null || mins <= 0) return '';
    if (mins < 1) return 'Menos de 1 min';
    return `${mins} min`;
  }

  ngOnInit(): void {
    // 0. Inicializar fecha/hora de sincronización de inmediato
    this.lastCheckedTime = new Date().toLocaleTimeString('es-MX', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });

    // 1. Obtener estado inicial conocido si ya está en memoria
    const currentUser = this.authService.getCurrentUser();
    if (currentUser) {
      this.applyStatusUpdate(currentUser);
    } else if (this.onboardingService.state.expediente.accountStatus) {
      this.currentAccountStatus = this.onboardingService.state.expediente.accountStatus;
    }

    // 2. Suscripción reactiva para capturar la respuesta de loadMe() cuando termine en segundo plano
    this.userSub = this.authService.user$.subscribe((user) => {
      if (user) {
        this.applyStatusUpdate(user);
        this.cdr.detectChanges();
      }
    });

    // 3. REGLA ESTRICTA DE ANIMACIÓN NAVBAR:
    const shouldPlay =
      this.onboardingService.state.expediente.playCompletionSequence === true ||
      sessionStorage.getItem('abaq_play_completion_anim') === 'true';

    // Desactivar de inmediato para que NUNCA vuelva a reproducirse en recargas
    this.onboardingService.setExpedienteState({ playCompletionSequence: false });
    sessionStorage.removeItem('abaq_play_completion_anim');

    if (shouldPlay && this.currentAccountStatus === 'pending') {
      this.seqStep = 'green';

      this.fadeTimer = setTimeout(() => {
        this.seqStep = 'fading';
        this.cdr.detectChanges();
      }, 1400);

      this.doneTimer = setTimeout(() => {
        this.seqStep = 'done';
        this.showConfettiBurst = true;
        this.cdr.detectChanges();
        this.confettiEndTimer = setTimeout(() => {
          this.showConfettiBurst = false;
          this.cdr.detectChanges();
        }, 3600);
      }, 2050);
    } else {
      this.seqStep = 'done';
    }

    // 4. Comprobar de inmediato el estado en la base de datos
    this.checkDbStatus(false);

    // 5. Polling periódico cada 4 segundos para detectar aprobación en tiempo real
    this.startPolling();
  }

  private startPolling(): void {
    this.stopPolling();
    if (this.currentAccountStatus !== 'pending') {
      return;
    }
    this.pollTimer = setInterval(() => {
      if (this.currentAccountStatus === 'pending') {
        this.checkDbStatus(false);
      } else {
        this.stopPolling();
      }
    }, 4000);
  }

  private stopPolling(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = undefined;
    }
  }

  ngOnDestroy(): void {
    if (this.userSub) this.userSub.unsubscribe();
    this.stopPolling();
    if (this.fadeTimer) clearTimeout(this.fadeTimer);
    if (this.doneTimer) clearTimeout(this.doneTimer);
    if (this.confettiEndTimer) clearTimeout(this.confettiEndTimer);
  }

  private applyStatusUpdate(data: {
    accountStatus?: 'pending' | 'active' | 'rejected' | 'inactive';
    folio?: string;
    fullName?: string;
    email?: string;
    cartaAceptacion?: string;
    comments?: string;
    lastStatusChange?: any;
    fechaInicioFormularioExpediente?: string;
    fechaFinFormularioExpediente?: string;
    fechaEmisionExpediente?: string;
    fechaAceptacionExpediente?: string;
    fechaExpedicionExpediente?: string;
    tiempoLlenadoMinutosExpediente?: number;
  } | null): void {
    if (!data) return;

    if (data.comments !== undefined && data.comments !== null) {
      this.coordinatorComments = data.comments;
    } else if (data.lastStatusChange?.comments) {
      this.coordinatorComments = data.lastStatusChange.comments;
    }

    const prevStatus = this.currentAccountStatus;

    if (data.accountStatus && data.accountStatus !== 'inactive') {
      this.currentAccountStatus = data.accountStatus as 'pending' | 'active' | 'rejected';
    }
    if (data.folio) {
      this.dbFolio = data.folio;
    }
    if (data.fullName) {
      this.dbFullName = data.fullName;
    }
    if (data.email) {
      this.dbEmail = data.email;
    }
    if (data.cartaAceptacion) {
      this.cartaAceptacionUrl = data.cartaAceptacion;
    }
    if (data.fechaInicioFormularioExpediente) {
      this.dbFechaInicioFormulario = data.fechaInicioFormularioExpediente;
    }
    if (data.fechaFinFormularioExpediente) {
      this.dbFechaFinFormulario = data.fechaFinFormularioExpediente;
    }
    if (data.fechaEmisionExpediente) {
      this.dbFechaEmision = data.fechaEmisionExpediente;
    }
    if (data.fechaAceptacionExpediente) {
      this.dbFechaAceptacion = data.fechaAceptacionExpediente;
    }
    if (data.fechaExpedicionExpediente) {
      this.dbFechaExpedicion = data.fechaExpedicionExpediente;
    }
    if (data.tiempoLlenadoMinutosExpediente !== undefined && data.tiempoLlenadoMinutosExpediente !== null) {
      this.dbTiempoLlenadoMinutos = data.tiempoLlenadoMinutosExpediente;
    }

    this.onboardingService.setExpedienteState({
      accountStatus: this.currentAccountStatus,
      folio: this.dbFolio || this.onboardingService.state.expediente.folio,
      fechaInicioFormularioExpediente: this.dbFechaInicioFormulario || this.onboardingService.state.expediente.fechaInicioFormularioExpediente,
      fechaFinFormularioExpediente: this.dbFechaFinFormulario || this.onboardingService.state.expediente.fechaFinFormularioExpediente,
      fechaEmisionExpediente: this.dbFechaEmision || this.onboardingService.state.expediente.fechaEmisionExpediente,
      fechaAceptacionExpediente: this.dbFechaAceptacion || this.onboardingService.state.expediente.fechaAceptacionExpediente,
      fechaExpedicionExpediente: this.dbFechaExpedicion || this.onboardingService.state.expediente.fechaExpedicionExpediente,
      tiempoLlenadoMinutosExpediente: this.dbTiempoLlenadoMinutos !== undefined ? this.dbTiempoLlenadoMinutos : this.onboardingService.state.expediente.tiempoLlenadoMinutosExpediente,
    });

    if (this.currentAccountStatus !== 'pending') {
      this.seqStep = 'done';
      if (this.fadeTimer) clearTimeout(this.fadeTimer);
      if (this.doneTimer) clearTimeout(this.doneTimer);
      this.stopPolling();
    }

    if (prevStatus === 'pending' && this.currentAccountStatus === 'active') {
      this.showConfettiBurst = true;
      setTimeout(() => {
        this.showConfettiBurst = false;
        this.cdr.detectChanges();
      }, 3500);
    }

    this.cdr.detectChanges();
  }

  checkDbStatus(manual = false): void {
    if (this.isCheckingStatus && !manual) {
      return;
    }
    this.isCheckingStatus = true;
    this.cdr.detectChanges();

    const hasToken = this.authService.hasToken();
    const queryFolio = this.dbFolio || this.folio;
    const queryEmail = this.dbEmail || this.email;

    const checkStream$ = hasToken
      ? this.authService.loadMe().pipe(
          timeout({ first: 3500 }),
          catchError(() => of(null)),
          switchMap((user) => {
            if (user) {
              return of(user);
            }
            if (queryFolio || queryEmail) {
              return this.authService.checkExpedienteStatus(queryFolio, queryEmail).pipe(
                timeout({ first: 3500 }),
                map((res) => res?.data || null),
                catchError(() => of(null)),
              );
            }
            return of(null);
          }),
        )
      : (queryFolio || queryEmail)
        ? this.authService.checkExpedienteStatus(queryFolio, queryEmail).pipe(
            timeout({ first: 3500 }),
            map((res) => res?.data || null),
            catchError(() => of(null)),
          )
        : of(null);

    checkStream$
      .pipe(
        finalize(() => {
          this.isCheckingStatus = false;
          this.lastCheckedTime = new Date().toLocaleTimeString('es-MX', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          });
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (data) => {
          if (data) {
            this.applyStatusUpdate(data);
          }
        },
        error: () => {
          this.cdr.detectChanges();
        },
      });
  }

  goToPlatform(): void {
    const user = this.authService.getCurrentUser();

    const navigateToHome = () => {
      if (user?.role === 'admin') {
        this.router.navigate(['/admin/home']);
      } else if (user?.role === 'volunteer') {
        this.router.navigate(['/volunteer-home']);
      } else {
        this.router.navigate(['/home']);
      }
    };

    // Fase D.3 (RA1-1): marcar primerIngresoRealizado=true ANTES de liberar al
    // Inicio del rol, para que el guard no vuelva a forzar /onboarding/expediente.
    // Navegar igual si el backend falla para no dejar atrapado al usuario; el
    // guard reintentará marcar en el siguiente ciclo si sigue en false.
    if (user?.role === 'student') {
      const studentId =
        (user as unknown as { studentId?: string }).studentId || user.id;
      this.studentService.markPrimerIngreso(studentId).subscribe({
        next: () => navigateToHome(),
        error: () => navigateToHome(),
      });
    } else if (user?.role === 'volunteer') {
      const volunteerId =
        (user as unknown as { volunteerId?: string }).volunteerId || user.id;
      this.volunteerService.markPrimerIngreso(volunteerId).subscribe({
        next: () => navigateToHome(),
        error: () => navigateToHome(),
      });
    } else {
      navigateToHome();
    }
  }

  copyFolio(): void {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(this.folio).then(() => {
        this.isCopied = true;
        this.cdr.detectChanges();
        setTimeout(() => {
          this.isCopied = false;
          this.cdr.detectChanges();
        }, 2000);
      });
    }
  }

  logout(): void {
    this.stopPolling();
    this.onboardingService.clearState();
    this.authService.logout().then(() => {
      this.router.navigate(['/auth/login']);
    });
  }
}
